import { getHostPageUrl } from '../../../utils/hostPageContext';
import { NormalizedProductType, ProductType } from '../types';

export function debounce<T extends (...args: any[]) => void>(
  fn: T,
  delay: number = 350
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

export async function hashSHA256(text: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function formatCurrency(amount: number): string {
  const formatted = new Intl.NumberFormat('de-DE').format(amount);
  return `+${formatted}€`;
}

export function normalizeProductType(productType?: ProductType): NormalizedProductType {
  if (!productType) return 'Photovoltaic';
  return productType.replace(' + ', '_') as NormalizedProductType;
}

export function hasAllSameDigits(value: string): boolean {
  if (!value || value.length <= 1) return false;
  const digits = value.replace(/\s/g, '');
  return new Set(digits).size === 1;
}

export function getUTMParams(): Record<string, string> {
  const url = new URL(getHostPageUrl());
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_keyword', 'utm_device', 'utm_adposition', 'utm_placement'];
  const params: Record<string, string> = {};

  utmKeys.forEach((key) => {
    const value = url.searchParams.get(key);
    if (value) {
      params[key] = value;
    }
  });

  return params;
}

export function isValidPhone(phone: string): boolean {
  const cleaned = phone.replace(/\s/g, '');
  const pattern = /^[\d]{10,18}$/;
  return pattern.test(cleaned) && !hasAllSameDigits(cleaned);
}

export function isValidZipCode(zip: string): boolean {
  return /^\d{5}$/.test(zip);
}

export function isValidEmail(email: string): boolean {
  const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return pattern.test(email);
}

export function splitFullName(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(/\s+/);
  const firstName = parts.shift() || '';
  const lastName = parts.join(' ') || '';
  return { firstName, lastName };
}

export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function saveZipFlag(flag: boolean | null): void {
  if (flag === null) {
    sessionStorage.removeItem('enpalZipFlag');
  } else {
    sessionStorage.setItem('enpalZipFlag', String(flag));
  }
}

export function getZipFlag(): boolean | null {
  const value = sessionStorage.getItem('enpalZipFlag');
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
}

export function getAgentCodePartner(): string | undefined {
  const url = new URL(window.location.href);
  const agentCode = url.searchParams.get('agent_code_partner');
  return agentCode || undefined;
}

export function getAgentCodeInstaller(): string | undefined {
  const url = new URL(window.location.href);
  const agentCode = url.searchParams.get('agent_code_installer');
  return agentCode || undefined;
}
